import * as React from 'react';
import { observer } from 'mobx-react';

import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { Employee } from 'nomst/Stores/Decision/employee';
import EligibilityProrationOption from './eligibilityProrationOption';
import { IEligibilityProrationOverrideStore } from 'typings/eligibilityProrationOverrideStore';
import { User } from 'nomst/Stores/User/user';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { StockAmount } from 'nomst/Stores/Decision/stockAmount';
import { AmountCode } from 'typings/enums';

export interface IEligibilityProrationModalProps {
    countryCurrency: string;
    user: User;
    configuration: Configuration;
    eligibilityOverride: IEligibilityProrationOverrideStore;
    closeModal: () => void;
    employee: Employee;
}

export class EligibilityProrationModal extends React.Component<IEligibilityProrationModalProps> {
    private _eligibilityStore: IEligibilityProrationOverrideStore;
    constructor(props: IEligibilityProrationModalProps) {
        super(props);
        this.handleCloseDialogBox = this.handleCloseDialogBox.bind(this);
        this.handleSaveOverride = this.handleSaveOverride.bind(this);
        this._eligibilityStore = this.props.eligibilityOverride;
    }
    render(): React.ReactNode {
        const { user } = this.props;
        if (user.isRoleOnlyManager) {
            return null;
        }

        return (
            <div className="pxl-modal eligibility-modal">
                <div className="pxl-modal__content">
                    <div className="eligibility-modal__header">
                        <h2 className="pxl-text-c2">Eligibility & Proration Override</h2>
                        <div className="close-button-container">
                            <button
                                onClick={this.handleCloseDialogBox}
                                className="close no-btn-style"
                            >
                                <i className="icon-cancel pxl-text-c11-7" />
                            </button>
                        </div>
                    </div>
                    <div className="table-wrapper">
                        <table>
                            <tbody>
                                {this.renderEligibilityProrationOptions()}
                            </tbody>
                        </table>
                    </div>
                    <div className="pxl-text-right">
                        <button className="pxl-link" onClick={this.handleCloseDialogBox}>
                            Cancel
                        </button>
                        <button
                            onClick={this.handleSaveOverride}
                            disabled={this._eligibilityStore.isSaving}
                            className="pxl-btn pxl-margin-left-large"
                        >
                            Save Changes
                        </button>
                        <i
                            hidden={!this._eligibilityStore.isSaving}
                            className="icon-animate-spin icon-spin"
                        />
                    </div>
                </div>
            </div>
        );
    }

    private handleCloseDialogBox(): void {
        this._eligibilityStore.employeeAmounts.forEach(amount => {
            amount.resetEligibleOverride();
            amount.resetProrationOverride();
        });
        this.props.closeModal();
    }

    private async handleSaveOverride(): Promise<void> {
        const { employee, countryCurrency, closeModal } = this.props;
        await this._eligibilityStore.save(countryCurrency, employee);
        return closeModal();
    }

    private renderEligibilityProrationOptions(): React.ReactNode {
        const { configuration } = this.props;

        return configuration.cycleBudgetFields.map((cycleBudgetField) =>
            this.renderEligibilityProrationOptionRow(cycleBudgetField));
    }

    private renderStockEligibilityProrationRow(stockAmount: StockAmount): React.ReactNode {
        return (
            <EligibilityProrationOption
                key={AmountCode.Stock}
                employeeAmount={stockAmount}
                cycleBudgetField={this.props.configuration.getCycleBudgetField(AmountCode.Stock)}
            />
        );
    }

    private renderEligibilityProrationOptionRow(cycleBudgetField: CycleBudgetField): React.ReactNode {
        if (cycleBudgetField.amountCode === AmountCode.Stock) {
            const { employee } = this.props;
            return employee.stockAmount ? this.renderStockEligibilityProrationRow(employee.stockAmount) : null;
        }
        const employeeAmount = this._eligibilityStore.employeeAmounts.find(amount =>
            amount.amountCode === cycleBudgetField.amountCode);

        if (!employeeAmount || !this.props.configuration.hasCycleBudget(employeeAmount)) {
            return null;
        }

        return (
            <EligibilityProrationOption
                key={employeeAmount.amountCode}
                employeeAmount={employeeAmount}
                cycleBudgetField={cycleBudgetField}
            />
        );
    }
}

export default observer(EligibilityProrationModal);